import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Plus, Send, Trash2, Sparkles, ArrowRight, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { apiFetch } from '../lib/api';
import sidebarBg from '../assets/sidebar-bg.png';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const DEBUG_CHAT = typeof window !== 'undefined'
  && import.meta.env.DEV
  && window.localStorage?.getItem('FILMPLY_CHAT_DEBUG') === '1';

type ThreadMessage = {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  isStreaming?: boolean;
};

type ChatThread = {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount?: number;
};

type ChatStatus = {
  remaining: number;
  isPro: boolean;
};

type ChatSessionDto = {
  id: string;
  title: string | null;
  started_at: string | null;
  last_message_at: string | null;
  message_count: number | null;
};

type ChatHistoryDto = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
};

const NUMBERED_ITEM_REGEX = /^(\d+)[.)]\s+(.+)$/;

const generateId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const mapSessionToThread = (session: ChatSessionDto): ChatThread => ({
  id: session.id,
  title: session.title || 'New chat',
  createdAt: session.started_at || new Date().toISOString(),
  updatedAt: session.last_message_at || session.started_at || new Date().toISOString(),
  messageCount: session.message_count ?? 0,
});

function renderInlineFormatting(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={`${keyPrefix}-${index}`} className="font-semibold text-teal-300">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={`${keyPrefix}-${index}`}>{part}</span>;
  });
}

function parseAssistantResponse(content: string): { paragraphs: string[]; points: string[] } {
  const lines = content
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) return { paragraphs: [], points: [] };

  const numberedMatches = lines
    .map((line, index) => ({ index, match: line.match(NUMBERED_ITEM_REGEX) }))
    .filter((entry): entry is { index: number; match: RegExpMatchArray } => !!entry.match);

  if (numberedMatches.length < 2) {
    return { paragraphs: lines, points: [] };
  }

  const startsWithOne = Number(numberedMatches[0].match[1]) === 1;
  if (!startsWithOne) {
    return { paragraphs: lines, points: [] };
  }

  const firstNumberedIndex = numberedMatches[0].index;
  const leadParagraphs = lines.slice(0, firstNumberedIndex);
  const lastNumberedIndex = numberedMatches[numberedMatches.length - 1].index;
  const tailParagraphs = lines.slice(lastNumberedIndex + 1);

  const numberedIndexSet = new Set(numberedMatches.map((entry) => entry.index));
  const points: string[] = [];
  let currentPoint = '';
  let sawAnyPoint = false;

  for (let i = firstNumberedIndex; i <= lastNumberedIndex; i += 1) {
    const line = lines[i];
    const numbered = numberedIndexSet.has(i);
    if (numbered) {
      if (currentPoint) {
        points.push(currentPoint.trim());
      }
      const match = line.match(NUMBERED_ITEM_REGEX);
      currentPoint = match?.[2]?.trim() || '';
      sawAnyPoint = true;
      continue;
    }

    // Attach in-between non-numbered lines (e.g., "Why: ...") to the active point.
    if (sawAnyPoint) {
      if (currentPoint) {
        currentPoint += ` ${line}`;
      } else {
        currentPoint = line;
      }
    }
  }

  if (currentPoint) {
    points.push(currentPoint.trim());
  }

  return { paragraphs: [...leadParagraphs, ...tailParagraphs], points: points.filter(Boolean) };
}

function renderAssistantResponse(content: string): ReactNode {
  const { paragraphs, points } = parseAssistantResponse(content);

  if (points.length === 0) {
    return (
      <p className="leading-relaxed whitespace-pre-wrap">
        {renderInlineFormatting(content, 'assistant-plain')}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {paragraphs.map((paragraph, index) => (
        <p key={`paragraph-${index}`} className="leading-relaxed">
          {renderInlineFormatting(paragraph, `paragraph-${index}`)}
        </p>
      ))}
      <ol className="list-decimal pl-5 space-y-1.5">
        {points.map((point, index) => (
          <li key={`point-${index}`} className="leading-relaxed">
            {renderInlineFormatting(point, `point-${index}`)}
          </li>
        ))}
      </ol>
    </div>
  );
}

function renderAssistantStreaming(content: string): ReactNode {
  if (!content) return '...';
  return (
    <p className="leading-relaxed whitespace-pre-wrap">
      {renderInlineFormatting(content, 'assistant-streaming')}
    </p>
  );
}

function renderAssistantMessage(content: string, isStreaming?: boolean): ReactNode {
  if (isStreaming) {
    return renderAssistantStreaming(content);
  }
  return renderAssistantResponse(content);
}

const SUGGESTION_CHIPS = [
  { label: 'What should I watch tonight?', icon: '🎬' },
  { label: 'Movies like Interstellar', icon: '🚀' },
  { label: 'Best hidden gems on streaming', icon: '💎' },
  { label: 'Top rated thrillers this year', icon: '🔥' },
  { label: 'Help me pick a date night movie', icon: '❤️' },
];

const COLLAGE_POSTERS = [
  '/qJ2tW6WMUDux911BTUb0cABRcos.jpg', // Inception
  '/d5NXSklXo0qyIYkgV94XAgMIckC.jpg', // Interstellar
  '/gEU2QniE6E77NI6lCU6MxlNBvIx.jpg', // The Dark Knight
  '/pIkRyD18kl4FhoCNQuWxWu5cBLM.jpg', // The Matrix
  '/r2J02Z2OpNTctfOSN1Ydgii51I3.jpg', // The Godfather
  '/rSPw7tgCH9c6NqICZef4kZjFOQ5.jpg', // Pulp Fiction
  '/6oom5QYQ2yQTMJIbnvbkBL9cHo6.jpg', // Fight Club
  '/velWPhVMQeQKcxggNEU8YmIo52R.jpg', // The Shawshank Redemption
  '/8kNruSfhk5IoE4eZOc4UpqDJ67S.jpg', // Parasite
  '/udDclJoHjfjb8Ekgsd4FDteOkCU.jpg', // Joker
  '/q6y0Go1tsGEsmtFryDOJo3dEmqu.jpg', // The Shining
  '/aKuFiU82s5ISJpGDp4woUDNPSAo.jpg', // Blade Runner 2049
  '/taNth1aSEvEKQFp7AZFiNZBsRBQ.jpg', // Dune
  '/74xTEgt7R36Fpooo50r9T25onhq.jpg', // Oppenheimer
  '/hZkgoQYus5dXo3H8T7CYV25UmyB.jpg', // Breaking Bad
];

export function FilmplyPage() {
  const { session } = useAuth();
  const { pendingQuestion, clearPendingQuestion, closeChat } = useChat();

  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [messagesByThread, setMessagesByThread] = useState<Record<string, ThreadMessage[]>>({});
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [limitMessage, setLimitMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<ChatStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const isBackendEnabled = !!session?.access_token;
  const activeThreadIdRef = useRef<string | null>(activeThreadId);
  const titleCommittedRef = useRef<Set<string>>(new Set());
  const isCreatingThreadRef = useRef(false);
  const isSendingRef = useRef(false);

  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useEffect(() => {
    closeChat();
  }, [closeChat]);

  // Load backend chat sessions
  useEffect(() => {
    if (!isBackendEnabled) return;

    const loadSessions = async () => {
      setIsLoadingThreads(true);
      setError(null);
      try {
        const data = await apiFetch<{ sessions?: ChatSessionDto[] }>('/chat/sessions', {
          token: session?.access_token,
        });
        const sessionThreads = (data.sessions || []).map(mapSessionToThread);
        setThreads((prev) => {
          if (DEBUG_CHAT) {
            console.log('[CHAT_DEBUG] loadSessions', {
              serverIds: sessionThreads.map((t) => t.id),
              prevIds: prev.map((t) => t.id),
            });
          }
          const byId = new Map(prev.map((t) => [t.id, t]));
          const mergedServerThreads = sessionThreads.map((thread) => {
            const existing = byId.get(thread.id);
            const existingTitle = existing?.title?.trim();
            const serverTitle = thread.title?.trim();
            const title =
              existingTitle && existingTitle !== 'New chat' && (!serverTitle || serverTitle === 'New chat')
                ? existingTitle
                : thread.title || 'New chat';
            const updatedAt =
              existing && new Date(existing.updatedAt) > new Date(thread.updatedAt)
                ? existing.updatedAt
                : thread.updatedAt;
            const messageCount =
              typeof thread.messageCount === 'number' && typeof existing?.messageCount === 'number'
                ? Math.max(thread.messageCount, existing.messageCount)
                : thread.messageCount ?? existing?.messageCount;

            return {
              ...thread,
              title,
              updatedAt,
              messageCount,
            };
          });
          const serverIdSet = new Set(sessionThreads.map((t) => t.id));
          const localOnlyThreads = prev.filter((t) => !serverIdSet.has(t.id));

          if (DEBUG_CHAT && localOnlyThreads.length > 0) {
            console.log('[CHAT_DEBUG] loadSessions:preserve-local-only', {
              localOnlyIds: localOnlyThreads.map((t) => t.id),
            });
          }

          return [...mergedServerThreads, ...localOnlyThreads].sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        });
        setActiveThreadId((prev) => {
          if (prev) return prev;
          return sessionThreads[0]?.id ?? null;
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load chat sessions');
      } finally {
        setIsLoadingThreads(false);
      }
    };

    loadSessions();
  }, [isBackendEnabled, session?.access_token]);

  // Load history for active backend thread
  useEffect(() => {
    if (!isBackendEnabled || !activeThreadId) return;
    if (messagesByThread[activeThreadId]) return;

    const loadHistory = async () => {
      setIsLoadingHistory(true);
      setError(null);
      try {
        const data = await apiFetch<{ messages?: ChatHistoryDto[] }>(
          `/chat/history?session_id=${encodeURIComponent(activeThreadId)}`,
          { token: session?.access_token }
        );
        const historyMessages = (data.messages || []).map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: m.created_at,
        }));
        setMessagesByThread((prev) => {
          const existing = prev[activeThreadId];
          // Do not overwrite optimistic/live messages with eventually-consistent history.
          if (existing && existing.length > 0) {
            return prev;
          }
          return {
            ...prev,
            [activeThreadId]: historyMessages,
          };
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load chat history');
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [isBackendEnabled, activeThreadId, messagesByThread, session?.access_token]);

  const activeMessages = useMemo(() => {
    if (!activeThreadId) return [];
    return messagesByThread[activeThreadId] ?? [];
  }, [messagesByThread, activeThreadId]);

  // Auto-scroll to bottom when opening a thread or new messages arrive
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  }, [activeThreadId, activeMessages.length]);

  useEffect(() => {
    if (!isBackendEnabled || !session?.access_token) return;
    let cancelled = false;
    const loadStatus = async () => {
      try {
        const data = await apiFetch<{ remaining: number; isPro: boolean }>('/chat/status', {
          token: session.access_token,
        });
        if (!cancelled && typeof data.remaining === 'number') {
          setStatus({ remaining: data.remaining, isPro: !!data.isPro });
        }
      } catch {
        // Non-fatal: status updates also arrive from stream events.
      }
    };
    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [isBackendEnabled, session?.access_token]);

  const updateSessionTitle = useCallback(async (sessionId: string, title: string) => {
    if (!session?.access_token || !sessionId || !title.trim()) return;
    try {
      await apiFetch(`/chat/sessions/${sessionId}/title`, {
        method: 'PATCH',
        token: session.access_token,
        body: { title: title.slice(0, 80) },
      });
    } catch {
      // Non-fatal: title update is best-effort
    }
  }, [session?.access_token]);

  const createThreadOnServer = useCallback(async () => {
    if (!session?.access_token) {
      throw new Error('Sign in to start a chat.');
    }

    const data = await apiFetch<{ session: ChatSessionDto }>('/chat/sessions', {
      method: 'POST',
      token: session.access_token,
      body: { trigger: 'manual' },
    });
    return mapSessionToThread(data.session);
  }, [session?.access_token]);

  const addThread = useCallback(async () => {
    if (!isBackendEnabled) {
      setError('Sign in to start a chat.');
      return;
    }

    if (isCreatingThreadRef.current) return;
    isCreatingThreadRef.current = true;
    setIsCreatingThread(true);
    setError(null);

    try {
      const thread = await createThreadOnServer();
      setThreads((prev) => {
        const withoutDuplicate = prev.filter((t) => t.id !== thread.id);
        return [thread, ...withoutDuplicate];
      });
      setMessagesByThread((prev) => ({ ...prev, [thread.id]: prev[thread.id] ?? [] }));
      setActiveThreadId(thread.id);
      setLimitMessage(null);
      setInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create chat');
    } finally {
      setIsCreatingThread(false);
      isCreatingThreadRef.current = false;
    }
  }, [threads.length, isBackendEnabled, createThreadOnServer]);

  const deleteThread = useCallback(async (threadId: string) => {
    if (!isBackendEnabled || !session?.access_token) {
      setError('Sign in to manage chats.');
      return;
    }

    try {
      await apiFetch(`/chat/sessions/${threadId}`, {
        method: 'DELETE',
        token: session.access_token,
      });

      setThreads((prev) => {
        const next = prev.filter((t) => t.id !== threadId);
        if (activeThreadIdRef.current === threadId) {
          setActiveThreadId(next[0]?.id ?? null);
        }
        return next;
      });
      setMessagesByThread((prev) => {
        const next = { ...prev };
        delete next[threadId];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete chat');
    }
  }, [isBackendEnabled, session?.access_token]);

  const updateThreadMeta = useCallback((threadId: string, updater: (t: ChatThread) => ChatThread) => {
    setThreads((prev) => prev.map((t) => (t.id === threadId ? updater(t) : t)));
  }, []);

  const sendToActiveThread = useCallback(async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed) return;
    if (isSendingRef.current) return;
    isSendingRef.current = true;

    setError(null);

    if (!isBackendEnabled) {
      setError('Sign in to use Filmply chat.');
      isSendingRef.current = false;
      return;
    }

    if (!session?.access_token) {
      setError('Sign in to use Filmply chat.');
      isSendingRef.current = false;
      return;
    }
    if (status && !status.isPro && status.remaining <= 0) {
      setError("You've reached your monthly message limit. Upgrade to Pro for unlimited chat.");
      isSendingRef.current = false;
      return;
    }

    const sendAttemptId = generateId();
    if (DEBUG_CHAT) {
      console.log('[CHAT_DEBUG] send:start', {
        sendAttemptId,
        activeThreadId: activeThreadIdRef.current,
        input: trimmed,
      });
    }

    const now = new Date().toISOString();
    const userMessage: ThreadMessage = {
      id: generateId(),
      role: 'user',
      content: trimmed,
      createdAt: now,
    };
    const assistantMessage: ThreadMessage = {
      id: generateId(),
      role: 'assistant',
      content: '',
      createdAt: now,
      isStreaming: true,
    };

    let threadId = activeThreadId;
    let createdThreadTitle: string | null = null;
    if (!threadId) {
      try {
        const newThread = await createThreadOnServer();
        threadId = newThread.id;
        createdThreadTitle = newThread.title || 'New chat';
        setThreads((prev) => [newThread, ...prev.filter((t) => t.id !== newThread.id)]);
        setMessagesByThread((prev) => ({ ...prev, [newThread.id]: prev[newThread.id] ?? [] }));
        setActiveThreadId(newThread.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create chat');
        isSendingRef.current = false;
        return;
      }
    }

    setMessagesByThread((prev) => ({
      ...prev,
      [threadId]: [...(prev[threadId] ?? []), userMessage, assistantMessage],
    }));

    const currentThreadTitle = createdThreadTitle ?? (threads.find((t) => t.id === threadId)?.title || 'New chat');
    const shouldCommitTitle = currentThreadTitle === 'New chat';
    updateThreadMeta(threadId, (thread) => ({
      ...thread,
      updatedAt: now,
      title: shouldCommitTitle ? trimmed.slice(0, 48) : thread.title,
    }));

    setInput('');
    setIsSending(true);

    const commitSessionTitle = () => {
      if (!shouldCommitTitle || !threadId || !trimmed) return;
      if (titleCommittedRef.current.has(threadId)) return;
      titleCommittedRef.current.add(threadId);
      const nextTitle = trimmed.slice(0, 48) || 'New chat';
      updateSessionTitle(threadId, nextTitle);
      updateThreadMeta(threadId, (thread) => ({
        ...thread,
        title: nextTitle,
      }));
    };
    commitSessionTitle();

    let streamHadError = false;
    try {
      const response = await fetch(`${API_BASE_URL}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          message: trimmed,
          session_id: threadId,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const errorText = data.error || 'Failed to send message';
        setError(errorText);
        setMessagesByThread((prev) => ({
          ...prev,
          [threadId]: (prev[threadId] ?? []).map((m, idx, arr) => {
            if (idx !== arr.length - 1) return m;
            if (m.role !== 'assistant') return m;
            return {
              ...m,
              isStreaming: false,
              content: m.content || errorText,
            };
          }),
        }));
        setIsSending(false);
        isSendingRef.current = false;
        return;
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let messageId: string | undefined;

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const event of events) {
            if (!event.trim()) continue;

            const lines = event.split('\n');
            let eventType = '';
            let eventData = '';

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7);
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6);
              }
            }

            if (!eventType || !eventData) continue;

            try {
              const data = JSON.parse(eventData);

              if (eventType === 'session') {
                if (typeof data.remaining === 'number') {
                  setStatus({ remaining: data.remaining, isPro: !!data.isPro });
                }
              } else if (eventType === 'chunk') {
                setMessagesByThread((prev) => {
                  const next = { ...prev };
                  const threadMessages = [...(next[threadId] ?? [])];
                  const lastIdx = threadMessages.length - 1;
                  const lastMsg = threadMessages[lastIdx];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    threadMessages[lastIdx] = {
                      ...lastMsg,
                      content: lastMsg.content + (data.text || ''),
                    };
                    next[threadId] = threadMessages;
                  }
                  return next;
                });
              } else if (eventType === 'replace') {
                if (typeof data.text === 'string') {
                  setMessagesByThread((prev) => {
                    const next = { ...prev };
                    const threadMessages = [...(next[threadId] ?? [])];
                    const lastIdx = threadMessages.length - 1;
                    const lastMsg = threadMessages[lastIdx];
                    if (lastMsg && lastMsg.role === 'assistant') {
                      threadMessages[lastIdx] = {
                        ...lastMsg,
                        content: data.text,
                      };
                      next[threadId] = threadMessages;
                    }
                    return next;
                  });
                }
              } else if (eventType === 'done') {
                messageId = data.message_id;
                if (typeof data.remaining === 'number') {
                  setStatus((prev) => {
                    if (prev) return { ...prev, remaining: data.remaining };
                    const inferredIsPro = typeof data.isPro === 'boolean' ? data.isPro : data.remaining === -1;
                    return { remaining: data.remaining, isPro: inferredIsPro };
                  });
                }
                if (typeof data.final_text === 'string' && data.final_text.trim().length > 0) {
                  setMessagesByThread((prev) => {
                    const next = { ...prev };
                    const threadMessages = [...(next[threadId] ?? [])];
                    const lastIdx = threadMessages.length - 1;
                    const lastMsg = threadMessages[lastIdx];
                    if (lastMsg && lastMsg.role === 'assistant') {
                      threadMessages[lastIdx] = {
                        ...lastMsg,
                        content: data.final_text,
                      };
                      next[threadId] = threadMessages;
                    }
                    return next;
                  });
                }
              } else if (eventType === 'error') {
                streamHadError = true;
                const errorText = data.error || 'Something went wrong';
                setError(errorText);
                setMessagesByThread((prev) => {
                  const next = { ...prev };
                  const threadMessages = [...(next[threadId] ?? [])];
                  const lastIdx = threadMessages.length - 1;
                  const lastMsg = threadMessages[lastIdx];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    threadMessages[lastIdx] = { ...lastMsg, isStreaming: false, content: errorText };
                    next[threadId] = threadMessages;
                  }
                  return next;
                });
              }
            } catch {
              // ignore parse errors
            }
          }

          if (streamHadError) {
            break;
          }
        }
      }

      if (!streamHadError) {
        setMessagesByThread((prev) => {
          const next = { ...prev };
          const threadMessages = [...(next[threadId] ?? [])];
          const lastIdx = threadMessages.length - 1;
          const lastMsg = threadMessages[lastIdx];
          if (lastMsg && lastMsg.role === 'assistant') {
            threadMessages[lastIdx] = { ...lastMsg, isStreaming: false, id: messageId };
            next[threadId] = threadMessages;
          }
          return next;
        });
      }
    } catch {
      setError('Failed to send message. Please try again.');
      setMessagesByThread((prev) => ({
        ...prev,
        [threadId]: (prev[threadId] ?? []).map((m, idx, arr) => {
          if (idx !== arr.length - 1) return m;
          if (m.role !== 'assistant') return m;
          return {
            ...m,
            isStreaming: false,
            content: m.content || 'Failed to send message. Please try again.',
          };
        }),
      }));
    } finally {
      setIsSending(false);
      isSendingRef.current = false;
    }
  }, [activeThreadId, isBackendEnabled, session?.access_token, status, threads, createThreadOnServer, updateThreadMeta, updateSessionTitle]);

  useEffect(() => {
    if (!pendingQuestion) return;
    sendToActiveThread(pendingQuestion);
    clearPendingQuestion();
  }, [pendingQuestion, sendToActiveThread, clearPendingQuestion]);

  const showWelcomeView = !isLoadingHistory && activeMessages.length === 0;

  return (
    <div className="h-[calc(100vh-64px)] overflow-hidden bg-[#111111]">
      <div className="h-full flex border-t border-white/[0.04]">
        <aside className="w-[300px] max-w-[40%] min-w-[240px] border-r border-white/[0.04] flex flex-col relative overflow-hidden">
          {/* Sidebar background image */}
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.18] pointer-events-none z-0"
            style={{ backgroundImage: `url(${sidebarBg})` }}
            aria-hidden="true"
          />
          {/* Dark overlay to keep text readable — no red tint */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1419]/85 via-[#0F1419]/75 to-[#0F1419]/85 pointer-events-none z-0" />

          {/* Filmply sidebar header with logo */}
          <div className="relative z-[1] px-4 pt-4 pb-3 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm shadow-teal-500/20">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-white font-semibold text-sm tracking-tight">Filmply</span>
          </div>

          {/* New chat button — left-aligned, no border below */}
          <div className="relative z-[1] px-3 pb-3">
            <button
              type="button"
              onClick={addThread}
              disabled={!isBackendEnabled || isCreatingThread}
              className="flex items-center gap-2 rounded-lg bg-white/[0.05] backdrop-blur-sm text-zinc-200 hover:bg-white/[0.08] hover:text-white disabled:opacity-50 px-3 py-2 text-sm font-medium transition-all"
            >
              <Plus size={14} />
              New chat
            </button>
            {limitMessage && (
              <p className="text-xs text-amber-300 mt-3 px-1">{limitMessage}</p>
            )}
          </div>

          <div className="relative z-[1] flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
            {isLoadingThreads ? (
              <p className="text-sm text-zinc-400 px-3 py-2">Loading chats...</p>
            ) : threads.length === 0 ? (
              <p className="text-sm text-zinc-500 px-3 py-2">No chats yet.</p>
            ) : (
              threads.map((thread) => (
                <div
                  key={thread.id}
                  className={`group flex items-center gap-2 rounded-lg px-3 py-2.5 transition-all ${
                    thread.id === activeThreadId
                      ? 'bg-white/[0.08] text-white border-l-2 border-teal-400'
                      : 'hover:bg-white/[0.04] text-zinc-400'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setActiveThreadId(thread.id)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <p className="truncate text-[15px] font-medium">{thread.title || 'New chat'}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteThread(thread.id)}
                    className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-rose-300 transition-all"
                    aria-label="Delete chat"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>

        <section className="flex-1 min-w-0 flex flex-col bg-[#111111] relative overflow-hidden">
          {/* Movie collage background — always visible, dims during conversation */}
          <div className={`absolute inset-0 grid grid-cols-5 gap-1 pointer-events-none z-0 transition-opacity duration-500 ${showWelcomeView ? 'opacity-[0.12]' : 'opacity-[0.04]'}`} aria-hidden="true">
            {COLLAGE_POSTERS.map((poster, i) => (
              <div
                key={i}
                className="bg-cover bg-center"
                style={{
                  backgroundImage: `url(https://image.tmdb.org/t/p/w342${poster})`,
                }}
              />
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/70 via-[#111111]/50 to-[#111111]/70 pointer-events-none z-0" />

          {/* Floating top bar: upgrade right */}
          <div className="absolute top-0 left-0 right-0 z-10 px-5 py-3 flex items-center justify-end">
            <Link
              to="/pricing"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.1] bg-white/[0.05] backdrop-blur-sm text-zinc-300 text-xs font-medium hover:bg-white/[0.08] hover:text-white transition-all"
            >
              <Crown size={12} className="text-amber-400" />
              {status?.isPro ? 'Pro Plan' : 'Upgrade Plan'}
            </Link>
          </div>

          {showWelcomeView ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-[1]">
              <div className="flex flex-col items-center w-full">
                <div className="mb-10 flex flex-col items-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mb-6 shadow-lg shadow-teal-500/20">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-2">
                    What can I help you find?
                  </h2>
                  <p className="text-base text-zinc-500">
                    Your AI-powered film companion
                  </p>
                </div>

                <div className="w-full max-w-xl mb-6">
                  <div className="relative">
                    <textarea
                      value={input}
                      onChange={(event) => setInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          sendToActiveThread(input);
                        }
                      }}
                      placeholder={isBackendEnabled ? 'Ask about movies, get recommendations...' : 'Sign in to chat with Filmply...'}
                      rows={1}
                      disabled={!isBackendEnabled}
                      className="w-full rounded-2xl bg-[#1A1F26]/80 backdrop-blur-sm border border-white/[0.1] text-white pl-5 pr-14 py-5 text-[17px] resize-none focus:outline-none focus:border-teal-500/40 focus:ring-1 focus:ring-teal-500/20 transition-all placeholder:text-zinc-600"
                    />
                    <button
                      type="button"
                      onClick={() => sendToActiveThread(input)}
                      disabled={!input.trim() || isSending || !isBackendEnabled}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-xl bg-teal-500 text-white flex items-center justify-center disabled:opacity-30 hover:bg-teal-400 transition-colors"
                      aria-label="Send message"
                    >
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap justify-center gap-2.5 max-w-xl">
                  {SUGGESTION_CHIPS.map((chip) => (
                    <button
                      key={chip.label}
                      type="button"
                      onClick={() => {
                        setInput(chip.label);
                        sendToActiveThread(chip.label);
                      }}
                      disabled={!isBackendEnabled || isSending}
                      className="px-4 py-2 rounded-full border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm text-zinc-400 text-sm font-medium hover:bg-white/[0.08] hover:text-zinc-200 hover:border-white/[0.14] transition-all disabled:opacity-50"
                    >
                      <span className="mr-1.5">{chip.icon}</span>
                      {chip.label}
                    </button>
                  ))}
                </div>

                {status && !status.isPro && (
                  <div className="mt-8 text-center">
                    <p className="text-sm text-zinc-500">
                      {status.remaining > 0
                        ? `${status.remaining} free chats remaining`
                        : 'No free chats remaining'}
                      {' \u00B7 '}
                      <Link
                        to="/pricing"
                        className="text-teal-400 hover:text-teal-300 font-medium transition-colors"
                      >
                        Upgrade to Pro
                      </Link>
                    </p>
                  </div>
                )}

                {error && (
                  <div className="mt-4 max-w-xl w-full rounded-xl border border-rose-500/20 bg-rose-500/5 text-rose-300 px-4 py-2.5 text-sm">
                    {error}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto pt-14 relative z-[1]">
                <div className="max-w-4xl mx-auto px-5 py-5 space-y-5">
                  {error && (
                    <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 text-rose-300 px-4 py-2.5 text-sm">
                      {error}
                    </div>
                  )}

                  {isLoadingHistory ? (
                    <p className="text-base text-zinc-500">Loading conversation...</p>
                  ) : (
                    activeMessages.map((message) => (
                      <div
                        key={message.id || `${message.role}-${message.createdAt}`}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`rounded-2xl px-5 py-4 text-base leading-relaxed ${
                            message.role === 'user'
                              ? 'max-w-[85%] bg-teal-500/90 text-white rounded-br-md'
                              : 'w-full bg-white/[0.04] border-l-2 border-teal-500/30 text-zinc-200 rounded-l-none'
                          }`}
                        >
                          {message.role === 'assistant'
                            ? renderAssistantMessage(message.content, message.isStreaming)
                            : (
                              <p className="leading-relaxed whitespace-pre-wrap">
                                {message.content || (message.isStreaming ? '...' : '')}
                              </p>
                            )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="relative z-[1] bg-[#111111]/80 backdrop-blur-sm">
                <div className="max-w-4xl mx-auto px-5 py-5">
                  <div className="flex items-end gap-3">
                    <textarea
                      value={input}
                      onChange={(event) => setInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          sendToActiveThread(input);
                        }
                      }}
                      placeholder="Message filmply..."
                      rows={1}
                      disabled={!isBackendEnabled}
                      className="flex-1 rounded-2xl bg-[#1A1F26]/80 border border-white/[0.08] text-white px-5 py-4.5 text-[17px] resize-none focus:outline-none focus:border-teal-500/40 focus:ring-1 focus:ring-teal-500/20 transition-all placeholder:text-zinc-600"
                    />
                    <button
                      type="button"
                      onClick={() => sendToActiveThread(input)}
                      disabled={!input.trim() || isSending || !isBackendEnabled}
                      className="w-12 h-12 rounded-xl bg-teal-500 text-white flex items-center justify-center disabled:opacity-30 hover:bg-teal-400 transition-colors"
                      aria-label="Send message"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
